import React from 'react';
import Map, { Layer, Marker, NavigationControl, Source, MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export interface LocalMap2Props {
  latitude: number;
  longitude: number;
  zoom?: number;
  height?: number | string;
  borderRadius?: number | string;
  showMarker?: boolean;
  className?: string;
  style?: React.CSSProperties;
  markers?: MapMarker[];
  markerStyle?: React.CSSProperties;
  polygons?: MapPolygon[];
  mapStyle?: string;
  pitch?: number;
  bearing?: number;
  children?: React.ReactNode;
}

const baseContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(15,23,42,0.15)'
};

export interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  label?: string;
  subLabel?: string;
  color?: string;
}

export interface MapPolygon {
  id: string;
  coordinates: number[][][];
  fillColor?: string;
  fillOpacity?: number;
  lineColor?: string;
  lineWidth?: number;
  lineDasharray?: [number, number];
}

export const LocalMap2: React.FC<LocalMap2Props> = (props) => {
  const {
    latitude,
    longitude,
    zoom = 8,
    height = 400,
    borderRadius = 12,
    showMarker = true,
    className,
    style,
    markers = [],
    markerStyle,
    polygons,
    mapStyle = 'mapbox://styles/mapbox/streets-v12',
    pitch = 0,
    bearing = 0,
    children
  } = props;

  const mapRef = React.useRef<MapRef | null>(null);
  const [mapReady, setMapReady] = React.useState(false);

  React.useEffect(() => {
    const mapInstance = mapRef.current;
    if (!mapInstance || !mapReady) return;

    mapInstance.flyTo({
      center: [longitude, latitude],
      zoom,
      pitch,
      bearing,
      duration: 1000,
      essential: true
    });
  }, [latitude, longitude, zoom, pitch, bearing, mapReady]);

  const handleMapLoad = () => setMapReady(true);

  if (!MAPBOX_TOKEN) {
    console.error('Missing Mapbox token');
    return (
      <div
        className={className}
        style={{
          ...baseContainerStyle,
          height,
          borderRadius,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f172a',
          color: '#f8fafc',
          ...style
        }}
      >
        Map unavailable. Add REACT_APP_MAPBOX_TOKEN to your .env and restart.
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        ...baseContainerStyle,
        height,
        borderRadius,
        ...style
      }}
    >
      <Map
        ref={mapRef}
        onLoad={handleMapLoad}
        mapboxAccessToken={MAPBOX_TOKEN}
        mapStyle={mapStyle}
        initialViewState={{ latitude, longitude, zoom, pitch, bearing }}
        style={{ width: '100%', height: '100%' }}
      >
        {polygons?.map((poly) => (
          <Source
            key={poly.id}
            id={`${poly.id}-source`}
            type="geojson"
            data={{
              type: 'Feature',
              geometry: {
                type: 'Polygon',
                coordinates: poly.coordinates
              },
              properties: {}
            }}
          >
            <Layer
              id={`${poly.id}-fill`}
              type="fill"
              paint={{
                'fill-color': poly.fillColor ?? '#f472b6',
                'fill-opacity': poly.fillOpacity ?? 0.15
              }}
            />
            <Layer
              id={`${poly.id}-line`}
              type="line"
              paint={{
                'line-color': poly.lineColor ?? '#f43f5e',
                'line-width': poly.lineWidth ?? 2,
                'line-dasharray': poly.lineDasharray ?? [2, 2]
              }}
            />
          </Source>
        ))}
        <NavigationControl position="bottom-right" showCompass={false} />
        {showMarker && <Marker latitude={latitude} longitude={longitude} anchor="bottom" />}
        {markers.map((marker) => (
          <Marker
            key={marker.id}
            latitude={marker.latitude}
            longitude={marker.longitude}
            anchor="bottom"
          >
            <div
              style={{
                minWidth: 120,
                backgroundColor: '#ffffff',
                borderRadius: 999,
                border: `2px solid ${marker.color ?? '#2563eb'}`,
                padding: '4px 10px',
                boxShadow: '0 6px 18px rgba(15,23,42,0.18)',
                color: '#0f172a',
                fontWeight: 600,
                fontSize: 12,
                textAlign: 'center' as React.CSSProperties['textAlign'],
                ...markerStyle
              }}
            >
              {marker.label}
              {marker.subLabel && (
                <span
                  style={{
                    display: 'block',
                    fontSize: 10,
                    fontWeight: 500,
                    color: '#475569'
                  }}
                >
                  {marker.subLabel}
                </span>
              )}
            </div>
          </Marker>
        ))}
        {children}
      </Map>
    </div>
  );
};

export default LocalMap2;